<template>
  <div v-rect-loading="isLoading" class="custom-field-mapping-editor">
    <div class="editor-header">
      <span>{{ headerTitle }}</span>
    </div>
    <slot name="mappingTypeSwitchContainer"></slot>
    <slot name="jobCustomFieldMappingType">
      <div v-if="showDefaultValue" class="default-value-container">
        <label class="default-value-label">{{ $t(`${i18nPrefix}.label.defaultValue`) }}</label>
        <div class="default-value">
          <slot name="defaultValue" refName="defaultValue"></slot>
        </div>
      </div>
      <div v-if="jobCustomField.show" class="job-custom-field-container">
        <label class="job-custom-field-label">{{ $t(`${i18nPrefix}.label.jobCustomField`) }}</label>
        <div class="job-custom-field">
          <slot :name="jobCustomField.slotName">
            <el-select
              v-model="jobCustomField.fieldId"
              clearable
              filterable
              :multiple="jobCustomField.multiple"
              :multiple-limit="jobCustomField.multipleLimit"
              @change="handleJobCustomFieldChange"
            >
              <el-option
                v-for="(item, index) in jobCustomField.fieldList"
                :key="index"
                :label="item.i18nName"
                :value="item.id"
              ></el-option>
            </el-select>
          </slot>
        </div>
      </div>
      <TableList
        noHead
        noAction
        class="mapping-list-container"
        :showCreateBtn="false"
        :listData="mappingList"
        :tableColumns="tableColumns"
      >
        <template #selfFieldRender="{ row, data }">
          <slot name="selfFieldRender" :refName="`self_${row.id}`" :row="row" :data="data"></slot>
        </template>
        <template #otherFieldRender="{ row, data }">
          <slot name="otherFieldRender" :refName="`other_${row.id}`" :row="row" :data="data"></slot>
        </template>
      </TableList>
    </slot>
    <div v-if="showAddMapItemBtn" class="mapping-list-bottom-btn-container">
      <el-button class="add-map-item-btn" type="text" @click="handleAddMapItem">{{ $t(`${i18nPrefix}.btn.addMapItem`) }}</el-button>
    </div>
    <div class="bottom-btn-group">
      <el-button class="cancel-btn" @click="handleCancel">{{ $t('common.btn.cancel') }}</el-button>
      <el-button class="save-btn" @click="handleSave">{{ $t('common.btn.save') }}</el-button>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import TableList from '@/components/tableList/allowDragSortTable.vue'

const MAPPING_TYPE = {
  JOB_CUSTOM_FIELDS: 'JOB_CUSTOM_FIELDS',
  CUSTOM_MAPPING: 'CUSTOM_MAPPING'
}

export default {
  components: {
    TableList
  },
  props: {
    mappingList: {
      type: Array,
      required: true
    },
    fieldTypeInfo: {
      required: true
    },
    fieldInfo: {
      required: true
    },
    loading: {
      type: Boolean,
      default: false,
      required: true
    },
    showDefaultValue: {
      type: Boolean,
      default: false
    },
    showAddMapItemBtn: {
      type: Boolean,
      default: false
    },
    jobCustomField: {
      type: Object,
      default () {
        return {
          show: false,
          slotName: null,
          field: null,
          fieldId: null,
          fieldList: []
        }
      }
    },
    selfFieldWidth: {
      required: false
    },
    otherFieldWidth: {
      required: false
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.company.jobPosting',
      MAPPING_TYPE
    }
  },
  computed: {
    isLoading: {
      get () {
        return this.loading
      },
      set (val) {
        this.$emit('update:loading', val)
      }
    },
    channelName () {
      return this.fieldTypeInfo.channel?.i18nName
    },
    headerTitle () {
      const channelName = this.channelName
      const fieldTypeName = this.fieldTypeInfo.i18nName
      return this.$t(`${this.i18nPrefix}.title.mappingManagement`, { channelName, fieldTypeName })
    },
    tableColumns () {
      const channelName = this.channelName
      const columns = [
        {
          labelI18nKey: `${this.i18nPrefix}.label.selfField`,
          dataKey: 'self',
          width: this.selfFieldWidth,
          cellClass: 'self-field',
          slotName: 'selfFieldRender'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.otherField`, { channelName }),
          dataKey: 'other',
          width: this.otherFieldWidth,
          cellClass: 'other-field',
          slotName: 'otherFieldRender'
        }

      ]
      return columns
    }
  },
  created () {

  },
  methods: {
    handleCancel () {
      this.$router.go(-1)
    },
    handleSave () {
      this.$emit('save')
    },
    handleAddMapItem () {
      this.$emit('addMapItem')
    },
    handleJobCustomFieldChange (fieldId) {
      if (this.jobCustomField.multiple) {
        fieldId = fieldId && fieldId.length > 0 ? fieldId : null
        let fieldIdMap = this.jobCustomField.fieldIdMap || null
        if (!fieldIdMap) {
          fieldIdMap = this.jobCustomField.fieldIdMap = {}
          _.forEach(this.jobCustomField.fieldList, field => {
            fieldIdMap[field.id] = field
          })
        }
        const fieldList = fieldId ? [] : null
        _.forEach(fieldId, (id) => {
          fieldList.push(fieldIdMap[id])
        })
        this.jobCustomField.field = fieldList
      } else {
        this.jobCustomField.field = _.find(this.jobCustomField.fieldList, { id: fieldId })
      }
      this.$emit('update:showAddMapItemBtn', !fieldId)
      this.$emit('jobCustomFieldChange', fieldId)
    }
  }
}
</script>

<style lang="scss" scoped>
.custom-field-mapping-editor {
  .editor-header {
    color: #303133;
    line-height: 25px;
    font-size: 18px;
    font-weight: 500;
    margin-bottom: 20px;
  }

  .mapping-type-switch-container {
    display: flex;
    margin: 15px 0;

    .mapping-type-label {
      width: 110px;
      line-height: 32px;
      margin-right: 10px;
    }

    .mapping-type-value {
      flex: 1;
      height: 32px;
      line-height: 32px;

      ::v-deep {
        .has-error {
          .el-input__inner {
            border-color: #f56c6c;

            &:focus {
              border-color: #f56c6c;
            }
          }
        }

        .el-switch__label {
          height: 32px;
          line-height: 32px;
        }
      }
    }
  }

  .default-value-container {
    display: flex;
    margin: 15px 0;

    .default-value-label {
      width: 124px;
      line-height: 32px;
      margin-right: 10px;
    }

    .default-value {
      flex: 1;

      ::v-deep {
        .has-error {
          .el-input__inner {
            border-color: #f56c6c;

            &:focus {
              border-color: #f56c6c;
            }
          }
        }
      }
    }
  }

  .job-custom-field-container {
    display: flex;
    margin: 15px 0;

    .job-custom-field-label {
      width: 124px;
      line-height: 32px;
      margin-right: 10px;
    }

    .job-custom-field {
      flex: 1;

      .el-select {
        width: 100%;
      }
    }
  }

  .mapping-list-container {
    margin-top: 25px;

    ::v-deep {
      .table-header {
        display: none;
      }

      .el-table {
        td {
          padding: 10px 0;
        }

        .el-table__body {
          .has-error {
            .el-input__inner {
              border-color: #f56c6c;

              &:focus {
                border-color: #f56c6c;
              }
            }
          }

          .other-field {
            .el-input {
              .el-input__inner {
                padding: 0 8px;
              }
            }
          }
        }
      }
    }
  }

  .mapping-list-bottom-btn-container {
    text-align: center;
    height: 60px;
    line-height: 60px;
    border-bottom: 1px solid #ebeef5;

    .add-map-item-btn {
      padding-right: 10px;
      padding-left: 10px;
      background: #b2b2b2;
      color: #fff;
    }
  }

  .bottom-btn-group {
    display: flex;
    justify-content: space-between;
    margin-top: 30px;

    .el-button {
      padding: 8px 28px;
      border-radius: 2px;
      line-height: 22px;
      font-size: 16px;
      font-weight: 500;
      color: #0096cc;

      &.save-btn {
        margin-left: 24px;
        margin-right: 0;
        background: #0096cc;
        border-color: #0096cc;
        color: #ffffff;
      }
    }
  }
}
</style>
